import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {skipToken} from '@reduxjs/toolkit/query';
import {showNotification} from 'platform/components';

import {useCallback, useState} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  AvailableCarsFilterRequestBody,
  ROWS_PER_PAGE,
  sourcingApi,
  SourcingVehicleListApiArg,
  SourcingVehicleListApiResponse,
  SourcingVehicleListItemResponseBody,
  useSourcingVehicleListQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {useSourcingIntervalPoller} from '../../../hooks/useSourcingIntervalPoller';

// Per-tick poll page cap. See README §Real-time polling.
const MAX_POLL_PAGES = 5;

type UseClassifiedsVehicleListQueriesProps = {
  filterBody: AvailableCarsFilterRequestBody | null;
  orderBy: SourcingVehicleListApiArg['orderBy'];
  currency: SourcingVehicleListApiArg['currency'];
  pricingStrategy: SourcingVehicleListApiArg['pricingStrategy'];
  isFiltersOpen: boolean;
  isRealTime: boolean;
  page: number;
  pollingInterval: number;
  skip?: boolean;
};

export function useClassifiedsVehicleListQueries(props: UseClassifiedsVehicleListQueriesProps) {
  const {data, currentData, isLoading, isFetching, isError, error} = useSourcingVehicleListQuery(
    isNotNil(props.filterBody)
      ? {
          body: props.filterBody,
          page: props.page,
          orderBy: props.orderBy,
          currency: props.currency,
          pricingStrategy: props.pricingStrategy,
        }
      : skipToken,
    {skip: props.skip || props.isFiltersOpen}
  );

  // eslint-disable-next-line -eslint/no-explicit-any -- concrete RootState lives in the consuming app; matches the State/Extra typing used by `sourcingApiHelpers.ts` and `handleIncomingData.ts`.
  const dispatch = useDispatch<ThunkDispatch<any, any, UnknownAction>>();
  // eslint-disable-next-line -eslint/no-explicit-any -- same RootState reasoning as the dispatch type above.
  const store = useStore<any>();
  const [pollingFulfilledTimeStamp, setPollingFulfilledTimeStamp] = useState<number | undefined>(
    undefined
  );
  const [isPolling, setIsPolling] = useState(false);

  const {filterBody, currency, pricingStrategy, orderBy} = props;

  const reset = () => {
    if (isNil(filterBody)) {
      return;
    }

    const firstPageQueryArgs: SourcingVehicleListApiArg = {
      body: filterBody,
      page: 1,
      orderBy,
      currency,
      pricingStrategy,
    };

    dispatch(
      sourcingApi.util.updateQueryData('sourcingVehicleList', firstPageQueryArgs, (draft) => {
        draft.data = [];
        draft.links = {
          ...draft.links,
          offset: 0,
          size: 0,
        };
        draft.scrollAttemptedOffset = undefined;
        draft.isEndReached = undefined;
      })
    );
    dispatch(
      sourcingApi.endpoints.sourcingVehicleList.initiate(firstPageQueryArgs, {
        forceRefetch: true,
        subscribe: false,
      })
    );
  };

  const canPoll =
    !props.skip &&
    !props.isFiltersOpen &&
    props.isRealTime &&
    isNotNil(filterBody) &&
    isNotNil(currency);

  // useCallback for correctness (poller interval dep), not perf.
  const doPoll = useCallback(async () => {
    if (isNil(filterBody) || isNil(currency)) {
      return;
    }
    const pollArgsBase = {body: filterBody, currency, pricingStrategy, orderBy};
    const mainCacheKeyArgs: SourcingVehicleListApiArg = {...pollArgsBase, page: 1};

    // Snapshot cached adIds to detect overlap while walking poll pages. Empty cache → bail.
    const cachedItems =
      sourcingApi.endpoints.sourcingVehicleList.select(mainCacheKeyArgs)(store.getState()).data
        ?.data ?? [];
    if (cachedItems.length === 0) {
      return;
    }
    const seenAdIds = new Set(cachedItems.map((v) => v.sourcingVehicle.adId));

    const collected: SourcingVehicleListItemResponseBody[] = [];
    let overlapFound = false;
    let endOfFeed = false;

    setIsPolling(true);
    try {
      for (let pageNum = 1; pageNum <= MAX_POLL_PAGES && !overlapFound && !endOfFeed; pageNum++) {
        const subscription = dispatch(
          sourcingApi.endpoints.sourcingVehicleListPoll.initiate(
            {...pollArgsBase, page: pageNum},
            {forceRefetch: true}
          )
        );
        try {
          const result = await subscription.unwrap();
          // eslint-disable-next-line max-depth -- Pre-existing violation
          for (const vehicle of result.data) {
            // eslint-disable-next-line max-depth -- Pre-existing violation
            if (seenAdIds.has(vehicle.sourcingVehicle.adId)) {
              overlapFound = true;
              break;
            }
            seenAdIds.add(vehicle.sourcingVehicle.adId);
            collected.push(vehicle);
          }
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (result.data.length < ROWS_PER_PAGE) {
            endOfFeed = true;
          }
        } finally {
          subscription.unsubscribe();
        }
      }

      if (collected.length > 0) {
        dispatch(
          sourcingApi.util.updateQueryData(
            'sourcingVehicleList',
            mainCacheKeyArgs,
            (draft: SourcingVehicleListApiResponse) => {
              draft.data.unshift(...collected);
            }
          )
        );
      }
      // Cap hit: more new vehicles than MAX_POLL_PAGES could cover. See README §Real-time polling.
      if (!overlapFound && !endOfFeed) {
        showNotification.warning(i18n.t('page.sourcing.notifications.realTimeUpdateTruncated'));
      }
      setPollingFulfilledTimeStamp(Date.now());
    } catch {
      // Next tick will retry.
    } finally {
      setIsPolling(false);
    }
  }, [dispatch, store, filterBody, currency, pricingStrategy, orderBy]);

  useSourcingIntervalPoller({
    enabled: canPoll,
    pollingInterval: props.pollingInterval,
    doPoll,
    resetKey: JSON.stringify({filterBody, currency, pricingStrategy, orderBy}),
  });

  return {
    fulfilledTimeStamp: pollingFulfilledTimeStamp,
    isPolling,
    data,
    currentData,
    scrollAttemptedOffset: currentData?.scrollAttemptedOffset,
    isEndReached: currentData?.isEndReached,
    reset,
    isLoading,
    isFetching,
    isError,
    error,
  };
}
